/**
 * BON-56 — Audit log API client. Mirrors GET /api/v1/audit/users.
 *
 * Cursor is a base64-encoded JSON `{changedAt, id}` opaque to the caller;
 * always pass the value `nextCursor` returned in the previous page response.
 */

import { apiFetch } from '../lib/apiFetch';

export interface AuditEventDTO {
  id: string;
  changedAt: string;
  actor: { userId: string; email: string | null };
  target: { userId: string; email: string | null; name: string | null };
  /** Raw JSON blob from the backend — the view-model decides how to render. */
  fieldChanges: unknown;
}

export interface AuditEventsPage {
  events: AuditEventDTO[];
  nextCursor: { changedAt: string; id: string } | null;
}

export interface ListAuditParams {
  action?: string;
  actorUserId?: string;
  targetUserId?: string;
  from?: string;
  to?: string;
  cursor?: { changedAt: string; id: string } | null;
  limit?: number;
  includeLowSignal?: boolean;
}

export class AuditApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AuditApiError';
  }
}

function buildQueryString(params: ListAuditParams): string {
  const qs = new URLSearchParams();
  const entries: Array<[string, string | undefined]> = [
    ['action', params.action],
    ['actorUserId', params.actorUserId],
    ['targetUserId', params.targetUserId],
    ['from', params.from],
    ['to', params.to],
    ['limit', typeof params.limit === 'number' ? String(params.limit) : undefined],
    [
      'includeLowSignal',
      params.includeLowSignal !== undefined ? String(params.includeLowSignal) : undefined,
    ],
    ['cursor', params.cursor ? btoa(JSON.stringify(params.cursor)) : undefined],
  ];
  for (const [k, v] of entries) if (v !== undefined) qs.set(k, v);
  return qs.toString();
}

export async function listAuditEvents(params: ListAuditParams = {}): Promise<AuditEventsPage> {
  const qs = buildQueryString(params);
  const url = `/api/v1/audit/users${qs ? `?${qs}` : ''}`;
  const response = await apiFetch(url);
  const json = (await response.json().catch(() => ({}))) as {
    status?: string;
    message?: string;
    events?: AuditEventDTO[];
    nextCursor?: AuditEventsPage['nextCursor'];
  };
  if (!response.ok) {
    throw new AuditApiError(response.status, json.message ?? response.statusText);
  }
  return {
    events: json.events ?? [],
    nextCursor: json.nextCursor ?? null,
  };
}
